import { isGifImage, isSvgImage } from '@/lib/assets-config';
import { pickStringUnion, readString } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';
import { DATA_WIDGET_BLOCK_TYPES, type PageBlock } from '@/types/block';
import { getColumnSpanMaxWidthClassName, shouldForceFullWidthBlock } from './block-layout';
import { resolveGroupLayout, shouldStretchGroupToFillParent } from './group-layout';

export type BlockStackAlign = 'start' | 'center' | 'end';
export type BlockStackPadding = 'none' | 'sm' | 'md' | 'lg';
export type BlockStackDirection = 'vertical' | 'horizontal';

export interface BlockStackLayoutConfig {
  direction: BlockStackDirection;
  align: BlockStackAlign;
  padding: BlockStackPadding;
  /** Strip vertical padding only; keeps horizontal padding from `padding`. */
  verticalPaddingNone?: boolean;
}

export const BLOCK_STACK_DIRECTION_DEFAULT: BlockStackDirection = 'vertical';
export const BLOCK_STACK_ALIGN_DEFAULT: BlockStackAlign = 'start';
export const BLOCK_STACK_PADDING_DEFAULT: BlockStackPadding = 'sm';
export const BLOCK_STACK_VERTICAL_PADDING_NONE = 'py-0 md:py-0';

const BLOCK_STACK_GAP_CLASSNAME = 'gap-4';

const BLOCK_STACK_DIRECTION_CLASSNAMES: Record<BlockStackDirection, string> = {
  vertical: 'flex-col',
  horizontal: 'flex-col md:flex-row md:flex-nowrap',
};

export const BLOCK_STACK_SPACING_CLASSNAMES: Record<
  BlockStackPadding,
  {
    padding: string;
    /** Full-bleed escape for the last block — escapes all sides including bottom. */
    fullBleedEscape: string;
    /** Full-bleed escape for a middle block — escapes sides only, no bottom margin. */
    fullBleedEscapeMiddle: string;
  }
> = {
  none: {
    padding: '',
    fullBleedEscape: '',
    fullBleedEscapeMiddle: '',
  },
  sm: {
    padding: 'px-3 py-3 md:px-4 md:py-4',
    // calc width extends the element to fill both sides; margins shift the left edge flush.
    fullBleedEscape: '-mx-3 -mb-3 w-[calc(100%+1.5rem)] md:-mx-4 md:-mb-4 md:w-[calc(100%+2rem)]',
    fullBleedEscapeMiddle: '-mx-3 w-[calc(100%+1.5rem)] md:-mx-4 md:w-[calc(100%+2rem)]',
  },
  md: {
    padding: 'px-4 py-4 md:px-6 md:py-6',
    fullBleedEscape: '-mx-4 -mb-4 w-[calc(100%+2rem)] md:-mx-6 md:-mb-6 md:w-[calc(100%+3rem)]',
    fullBleedEscapeMiddle: '-mx-4 w-[calc(100%+2rem)] md:-mx-6 md:w-[calc(100%+3rem)]',
  },
  lg: {
    padding: 'px-6 py-6 md:px-8 md:py-8',
    fullBleedEscape: '-mx-6 -mb-6 w-[calc(100%+3rem)] md:-mx-8 md:-mb-8 md:w-[calc(100%+4rem)]',
    fullBleedEscapeMiddle: '-mx-6 w-[calc(100%+3rem)] md:-mx-8 md:w-[calc(100%+4rem)]',
  },
};

const BLOCK_STACK_ALIGN_CLASSNAMES: Record<BlockStackAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
};

const BLOCK_STACK_PADDING_CLASSNAMES: Record<BlockStackPadding, string> = {
  none: '',
  sm: BLOCK_STACK_SPACING_CLASSNAMES.sm.padding,
  md: BLOCK_STACK_SPACING_CLASSNAMES.md.padding,
  lg: BLOCK_STACK_SPACING_CLASSNAMES.lg.padding,
};

const BLOCK_STACK_FULL_BLEED_ESCAPE_CLASSNAMES: Record<BlockStackPadding, string> = {
  none: '',
  sm: BLOCK_STACK_SPACING_CLASSNAMES.sm.fullBleedEscape,
  md: BLOCK_STACK_SPACING_CLASSNAMES.md.fullBleedEscape,
  lg: BLOCK_STACK_SPACING_CLASSNAMES.lg.fullBleedEscape,
};

const BLOCK_STACK_FULL_BLEED_ESCAPE_MIDDLE_CLASSNAMES: Record<BlockStackPadding, string> = {
  none: '',
  sm: BLOCK_STACK_SPACING_CLASSNAMES.sm.fullBleedEscapeMiddle,
  md: BLOCK_STACK_SPACING_CLASSNAMES.md.fullBleedEscapeMiddle,
  lg: BLOCK_STACK_SPACING_CLASSNAMES.lg.fullBleedEscapeMiddle,
};

const BLOCK_STACK_DIRECTION_VALUES = ['horizontal', 'vertical'] as const;
const BLOCK_STACK_PADDING_VALUES = ['none', 'sm', 'md', 'lg'] as const;
export const BLOCK_STACK_ALIGN_VALUES = ['start', 'center', 'end'] as const;

export function isFullBleedImageBlock(block: PageBlock): boolean {
  return block.blockType === 'image' && Boolean(block.props?.fullBleed);
}

export function resolveBlockStackLayout(
  props: Record<string, unknown> | null | undefined,
): BlockStackLayoutConfig {
  return {
    direction: pickStringUnion(
      readString(props?.blockDirection),
      BLOCK_STACK_DIRECTION_VALUES,
      BLOCK_STACK_DIRECTION_DEFAULT,
    ),
    align: pickStringUnion(
      readString(props?.blockAlign),
      BLOCK_STACK_ALIGN_VALUES,
      BLOCK_STACK_ALIGN_DEFAULT,
    ),
    padding: pickStringUnion(
      readString(props?.blockPadding),
      BLOCK_STACK_PADDING_VALUES,
      BLOCK_STACK_PADDING_DEFAULT,
    ),
  };
}

export function getBlockStackContainerClassName(config?: BlockStackLayoutConfig): string {
  const resolved = config || {
    direction: BLOCK_STACK_DIRECTION_DEFAULT,
    align: BLOCK_STACK_ALIGN_DEFAULT,
    padding: BLOCK_STACK_PADDING_DEFAULT,
  };

  return cn(
    'flex w-full',
    BLOCK_STACK_DIRECTION_CLASSNAMES[resolved.direction],
    BLOCK_STACK_GAP_CLASSNAME,
    BLOCK_STACK_ALIGN_CLASSNAMES[resolved.align],
    BLOCK_STACK_PADDING_CLASSNAMES[resolved.padding],
    resolved.verticalPaddingNone && BLOCK_STACK_VERTICAL_PADDING_NONE,
  );
}

export function getBlockStackTextAlignClassName(
  config?: BlockStackLayoutConfig,
): string | undefined {
  const resolvedAlign = config?.align ?? BLOCK_STACK_ALIGN_DEFAULT;

  if (resolvedAlign === 'center') {
    return 'text-center';
  }

  if (resolvedAlign === 'end') {
    return 'text-right';
  }

  return undefined;
}

export function shouldStretchTopLevelBlockWrapper({
  block,
  groupChildren,
  topLevelCount,
  isHorizontalStack,
}: {
  block: PageBlock;
  groupChildren: PageBlock[];
  topLevelCount: number;
  isHorizontalStack: boolean;
}): boolean {
  if (block.blockType !== 'group') {
    return false;
  }

  const topLevelGroupLayout = resolveGroupLayout(block, groupChildren);

  if (isHorizontalStack || topLevelCount < 1) {
    return false;
  }

  return shouldStretchGroupToFillParent(topLevelGroupLayout);
}

function shouldUseAutoWidthTopLevelWrapper({
  block,
  groupChildren,
  isHorizontalStack,
  stackAlign,
  shouldStretchWrapper,
  hasForceFullWidthDescendant,
}: {
  block: PageBlock;
  groupChildren: PageBlock[];
  isHorizontalStack: boolean;
  stackAlign: BlockStackAlign;
  shouldStretchWrapper: boolean;
  hasForceFullWidthDescendant: boolean;
}): boolean {
  if (
    isHorizontalStack ||
    shouldStretchWrapper ||
    shouldForceFullWidthBlock(block) ||
    isFullBleedImageBlock(block)
  ) {
    return false;
  }

  if (block.blockType === 'text') {
    return false;
  }

  if (DATA_WIDGET_BLOCK_TYPES.has(block.blockType)) {
    return false;
  }

  // Keep inline/content-width blocks like button from expanding in start-aligned sections.
  if (stackAlign === 'start' && block.blockType !== 'button') {
    return false;
  }

  // SVG and GIF images need w-full: SVGs have no intrinsic pixel dimensions (w-auto collapses
  // the aspect-ratio container), and GIFs render at their natural size which appears reduced.
  if (
    block.blockType === 'image' &&
    (isSvgImage(block.props?.image) || isGifImage(block.props?.image))
  ) {
    return false;
  }

  if (block.blockType === 'group') {
    if (hasForceFullWidthDescendant) {
      return false;
    }
    const groupLayout = resolveGroupLayout(block, groupChildren);
    return !groupLayout.isFloatLayout && groupLayout.direction !== 'horizontal';
  }

  return true;
}

function resolveTopLevelBlockWidthClassName(
  block: PageBlock,
  {
    isHorizontalStack,
    useAutoWidthInVerticalStack,
  }: {
    isHorizontalStack: boolean;
    useAutoWidthInVerticalStack: boolean;
  },
): string {
  const spanMax = getColumnSpanMaxWidthClassName(block.columnSpan);

  if (shouldForceFullWidthBlock(block)) {
    return 'w-full';
  }

  if (isHorizontalStack) {
    return cn('w-full md:w-full', spanMax);
  }

  if (useAutoWidthInVerticalStack) {
    return cn('w-auto max-w-full', spanMax);
  }

  return cn('w-full', spanMax);
}

export function getTopLevelBlockWrapperClassName({
  block,
  groupChildren,
  topLevelCount,
  isHorizontalStack,
  stackAlign,
  stackPadding,
  topLevelTextAlignClassName,
  isLastBlock,
  hasForceFullWidthDescendant = false,
}: {
  block: PageBlock;
  groupChildren: PageBlock[];
  topLevelCount: number;
  isHorizontalStack: boolean;
  stackAlign?: BlockStackAlign;
  stackPadding?: BlockStackPadding;
  topLevelTextAlignClassName?: string;
  isLastBlock: boolean;
  hasForceFullWidthDescendant?: boolean;
}): string {
  const shouldStretchWrapper = shouldStretchTopLevelBlockWrapper({
    block,
    groupChildren,
    topLevelCount,
    isHorizontalStack,
  });
  const resolvedStackAlign = stackAlign ?? BLOCK_STACK_ALIGN_DEFAULT;
  const resolvedStackPadding = stackPadding ?? BLOCK_STACK_PADDING_DEFAULT;
  const useAutoWidthInVerticalStack = shouldUseAutoWidthTopLevelWrapper({
    block,
    groupChildren,
    isHorizontalStack,
    stackAlign: resolvedStackAlign,
    shouldStretchWrapper,
    hasForceFullWidthDescendant,
  });

  const isFullBleed = !isHorizontalStack && isFullBleedImageBlock(block);

  return cn(
    'min-w-0',
    block.blockType === 'text' && !isHorizontalStack && 'w-full',
    block.blockType === 'text' && topLevelTextAlignClassName,
    shouldStretchWrapper && 'h-full flex-1 flex flex-col',
    isFullBleed
      ? isLastBlock
        ? BLOCK_STACK_FULL_BLEED_ESCAPE_CLASSNAMES[resolvedStackPadding]
        : BLOCK_STACK_FULL_BLEED_ESCAPE_MIDDLE_CLASSNAMES[resolvedStackPadding]
      : resolveTopLevelBlockWidthClassName(block, {
          isHorizontalStack,
          useAutoWidthInVerticalStack,
        }),
  );
}
